import { useState } from "react";
import { motion } from "framer-motion";
import { AlertTriangle, ArrowRight, Radio } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import Countdown from "./Countdown";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Cinematic preorder hero — the site's primary attention-grabber.
 * Full-width, image-backed, huge live countdown, integrated email form.
 */
export default function PreorderHero({ data, keyArt }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
      toast.error("Inserisci un indirizzo email valido");
      return;
    }
    setLoading(true);
    try {
      const { data: res } = await axios.post(`${API}/subscribers`, {
        email,
        source: data.source || "preorder",
      });
      if (res.status === "already_subscribed") {
        toast.info("Sei già iscritto agli avvisi.", {
          description: "Ti scriveremo al momento dell'apertura preordini.",
        });
      } else {
        toast.success("Iscrizione confermata.", {
          description: "Riceverai l'avviso quando i preordini apriranno.",
        });
      }
      setEmail("");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          "Iscrizione non riuscita. Riprova tra un attimo."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="preorder-notice-anchor"
      data-testid="preorder-hero"
      className="relative w-full overflow-hidden border-b border-white/10"
    >
      {/* Backdrop image with heavy overlay */}
      {keyArt && (
        <>
          <motion.img
            initial={{ scale: 1.12, opacity: 0.3 }}
            animate={{ scale: 1.02, opacity: 0.7 }}
            transition={{ duration: 3.6, ease: [0.22, 1, 0.36, 1] }}
            src={keyArt}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/85 to-black/50" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/40 to-black/70" />
        </>
      )}

      {/* diagonal caution accents */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage:
            "repeating-linear-gradient(45deg, #FACC15 0, #FACC15 10px, transparent 10px, transparent 40px)",
        }}
      />
      <div className="absolute inset-y-0 left-0 w-1 bg-accent" />

      <div className="relative max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 py-16 md:py-24 lg:py-28">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start"
        >
          {/* LEFT — announcement copy */}
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-5">
              <span className="relative flex items-center justify-center w-10 h-10 border border-accent/60 bg-black/40">
                <AlertTriangle size={16} className="text-accent" />
                <span className="absolute inset-0 border border-accent/40 animate-pulse" />
              </span>
              <span className="font-body text-[11px] uppercase tracking-[0.35em] text-accent">
                {data.eyebrow}
              </span>
              <span className="w-8 h-px bg-white/20 hidden md:inline-block" />
              <span className="hidden md:inline-flex items-center gap-2 border border-white/15 bg-black/40 backdrop-blur-sm px-3 py-1">
                <Radio size={11} className="text-accent" />
                <span className="font-body text-[10px] uppercase tracking-[0.3em] text-white/70">
                  LIVE
                </span>
              </span>
            </div>

            <h1
              data-testid="preorder-title"
              className="font-heading uppercase text-white text-5xl md:text-7xl lg:text-8xl leading-[0.9] mb-6"
            >
              {data.title}
            </h1>

            <p className="font-body text-base md:text-lg text-white/70 leading-relaxed max-w-2xl mb-8">
              {data.description}
            </p>

            {/* Date pill + duration/status */}
            <div className="flex flex-wrap items-center gap-3 mb-10">
              <div className="inline-flex items-center gap-3 border border-accent/60 bg-accent/[0.06] backdrop-blur-sm px-4 py-3">
                <span className="w-2 h-2 rounded-full bg-accent animate-pulse" />
                <span className="font-heading uppercase text-xl md:text-2xl tracking-[0.12em] text-accent">
                  {data.targetLabel}
                </span>
              </div>
            </div>

            {/* Email form */}
            <form
              onSubmit={submit}
              data-testid="preorder-form"
              className="relative border border-white/20 focus-within:border-accent transition-colors duration-300 bg-black/50 backdrop-blur-sm max-w-2xl"
            >
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="la-tua@email.com"
                data-testid="preorder-email-input"
                className="w-full bg-transparent text-white placeholder:text-white/30 font-body text-base md:text-lg px-5 py-5 pr-56 outline-none"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="preorder-submit"
                className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center gap-3 bg-accent text-black hover:bg-white transition-colors px-5 py-3 disabled:opacity-60"
              >
                <span className="font-heading text-sm md:text-base tracking-[0.15em] uppercase">
                  {loading ? "..." : data.cta}
                </span>
                <ArrowRight size={14} />
              </button>
            </form>
          </div>

          {/* RIGHT — massive live countdown */}
          <div className="lg:col-span-5">
            <div className="border border-white/15 bg-black/60 backdrop-blur-md p-6 md:p-8">
              <div className="flex items-center justify-between mb-6">
                <div className="font-body text-[10px] uppercase tracking-[0.35em] text-accent">
                  Apertura preordini
                </div>
                <div className="flex items-center gap-2 border border-white/15 bg-black/40 px-2.5 py-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
                  <span className="font-body text-[9px] uppercase tracking-[0.3em] text-white/60">
                    Live
                  </span>
                </div>
              </div>
              <div className="mb-6">
                <Countdown targetDate={data.targetDate} size="lg" testId="preorder-hero-countdown" />
              </div>
              <div className="border-t border-white/10 pt-5 space-y-2">
                <div className="flex items-center justify-between font-body text-[11px] uppercase tracking-[0.25em]">
                  <span className="text-white/50">Standard Edition</span>
                  <span className="text-white">14,99€</span>
                </div>
                <div className="flex items-center justify-between font-body text-[11px] uppercase tracking-[0.25em]">
                  <span className="text-accent">Collector Edition</span>
                  <span className="text-accent">29,99€</span>
                </div>
                <div className="flex items-center justify-between font-body text-[11px] uppercase tracking-[0.25em]">
                  <span className="text-white/50">Digital Deluxe</span>
                  <span className="text-white">14,99€</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
